import { Button } from "@repo/ui/components/ui/button";
import { Calendar } from "@repo/ui/components/ui/calendar";
import { cn } from "@repo/ui/lib/utils";
import { format, parseISO, startOfDay } from "date-fns";
import { CalendarIcon, type LucideIcon, MapPin } from "lucide-react";
import { timelineBlockSurface } from "@/shared/timeline";
import { Rail } from "./rail";
import { SessionTimeline } from "./session-timeline";
import { useScheduleSessionsContext } from "./use-schedule-sessions-context";

/**
 * The `scheduling` step: a resource panel (mini-calendar + room +
 * procedures) and the care-manager day rail for the active session. Two grid
 * cells (returned as a fragment so the parent grid lays them out), dimmed
 * together while auto-advancing.
 */
export function SchedulingStep() {
  const { state, actions } = useScheduleSessionsContext();
  const {
    activeSession,
    selectedDate,
    hasSchedule,
    isLoadingSchedule,
    candidates,
    isLoadingSlots,
    advancing,
    assignedDates,
    bounds,
    rooms,
    selectedRoom,
    placedCandidate,
  } = state;
  // Location is the session's room *requirement* (stable) until a slot is actually
  // placed — only then is a specific room chosen. Deriving it from the day's
  // candidate set would flip the label per date without any real decision.
  const requirementRoom = activeSession?.procedures.find((p) => p.roomLabel)?.roomLabel;
  const placedRoomId = placedCandidate?.placements.find((p) => p.resourceType === "Location")?.resourceId;
  const placedRoomName = placedRoomId ? rooms.find((r) => r.id === placedRoomId)?.name : undefined;
  const locationLabel = placedRoomName ?? requirementRoom;

  // Enforce session order on the calendar: no days before the previous session's
  // date, none after the next session's date.
  const disabledDays = [
    { before: new Date() },
    ...(bounds.minStart ? [{ before: startOfDay(parseISO(bounds.minStart)) }] : []),
    ...(bounds.maxEnd ? [{ after: parseISO(bounds.maxEnd) }] : []),
  ];

  const noOpenings = !!selectedDate && !isLoadingSlots && candidates.length === 0;

  return (
    <>
      {/* Resource panel */}
      <div
        className={cn(
          "scroll-fade scroll-fade-12 scrollbar-none flex min-w-0 flex-col gap-4 overflow-y-auto border-r p-4 transition-opacity duration-200",
          advancing && "opacity-40",
        )}
      >
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={actions.selectDate}
          disabled={disabledDays}
          modifiers={{ booked: assignedDates }}
          modifiersClassNames={{
            booked:
              "relative after:absolute after:bottom-1 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-success",
          }}
          className="w-full p-0"
          classNames={{ root: "w-full" }}
        />

        {!hasSchedule && !isLoadingSchedule && (
          <p className="text-xs text-muted-foreground">The care manager has no schedule configured.</p>
        )}

        {rooms.length > 1 && !placedCandidate ? (
          <div>
            <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">Location</p>
            <div className="flex flex-wrap gap-1.5">
              <RoomChip label="Any room" active={selectedRoom === null} onClick={() => actions.setRoom(null)} />
              {rooms.map((r) => (
                <RoomChip
                  key={r.id}
                  label={r.name}
                  active={selectedRoom === r.id}
                  onClick={() => actions.setRoom(r.id)}
                />
              ))}
            </div>
          </div>
        ) : (
          locationLabel && (
            <div>
              <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
                Location
              </p>
              <ResourceChip icon={MapPin} label={locationLabel} />
            </div>
          )
        )}

        {activeSession && (
          <div className="-mx-4">
            <SessionTimeline key={activeSession.orderSetId} orderSetId={activeSession.orderSetId} />
          </div>
        )}
      </div>

      {/* Day rail */}
      <div className="flex min-h-0 flex-col overflow-hidden">
        <div className="flex flex-col gap-2 border-b px-4 py-3">
          <div className="relative flex items-baseline justify-between pr-28">
            <div>
              {selectedDate ? (
                <p className="text-sm font-medium">{format(selectedDate, "EEEE, MMMM d")}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No date selected</p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {locationLabel && (
                <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {locationLabel}
                </span>
              )}
              {(placedCandidate || (activeSession && state.assignments[activeSession.orderSetId])) && (
                <Button
                  variant="secondary"
                  size="xs"
                  className="absolute right-0 top-1/2 -translate-y-1/2 bg-destructive/10 text-destructive hover:bg-destructive/15"
                  disabled={state.busy || advancing}
                  onClick={actions.clearActiveTime}
                >
                  Clear proposal
                </Button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span className={cn("size-2.5", timelineBlockSurface({ accent: "slate", variant: "delivered" }))} />
              Booked here
            </span>
            {/* The device band is a hatch behind the rail, not a block, so it
                keeps its own swatch. */}
            <span className="flex items-center gap-1.5">
              <span className="size-2.5 rounded-[2px] border border-dashed border-amber-500/70 bg-amber-100/60" />
              Device in use elsewhere
            </span>
            <span className="flex items-center gap-1.5">
              <span className={cn("size-2.5", timelineBlockSurface({ accent: "emerald", variant: "delivered" }))} />
              Your sessions
            </span>
          </div>
        </div>

        {!selectedDate && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center">
            <CalendarIcon className="size-5 text-muted-foreground/30" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">Select a date to see the day</p>
          </div>
        )}

        {selectedDate && (
          <div className="relative flex min-h-0 flex-1 flex-col">
            <Rail />
            {noOpenings && (
              <div className="pointer-events-none absolute inset-x-0 bottom-3 mx-auto w-fit rounded-full bg-muted px-3 py-1 text-[11px] text-muted-foreground">
                No openings on {format(selectedDate, "EEEE")}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

/** A room-filter pill. */
function RoomChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors",
        active
          ? "border-primary/40 bg-primary/[0.06] text-foreground"
          : "border-border text-muted-foreground hover:bg-muted",
      )}
    >
      {label}
    </button>
  );
}

/** A small read-only resource tag (room / device a procedure requires). */
function ResourceChip({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  return (
    <span
      title={label}
      className="inline-flex max-w-[7.5rem] items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[9px] font-medium text-muted-foreground"
    >
      <Icon className="size-2.5 shrink-0" strokeWidth={2} />
      <span className="truncate">{label}</span>
    </span>
  );
}
